import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ImageUp, ScanLine, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import ConfirmDialog from "@/components/ConfirmDialog";
import FileDropzone from "@/components/FileDropzone";
import Spinner from "@/components/Spinner";
import useOrganization from "../../hooks/useOrganization";
import { ACCEPTED_IMAGE_TYPES, deleteMasterImage, keepValidMasterImages } from "../../utils";
import { createMasterImageFromFile } from "../../masterImages/createMasterImage";
import { unappliedChanges } from "../../masterImages/changes";
import type { BrickSeries } from "../../masterImages/types";

interface Props {
  brickSeries: BrickSeries;
  onChange(): void;
}

const MasterImages: React.FC<Props> = ({ brickSeries, onChange }) => {
  const organization = useOrganization();
  const navigate = useNavigate();
  const [progress, setProgress] = useState<number | null>(null);
  const bricks = brickSeries.bricks ?? [];
  const masterImages = brickSeries.masterImages ?? [];
  const editorPath = (id: string) =>
    `/${organization}/series/${brickSeries._id}/masterImages/${id}`;

  const upload = async (files: File[]) => {
    const [file] = await keepValidMasterImages(files);
    if (!file) return;
    setProgress(0);
    try {
      const masterImage = await createMasterImageFromFile(
        organization,
        brickSeries._id,
        file,
        setProgress,
      );
      navigate(editorPath(masterImage._id));
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setProgress(null);
    }
  };

  const remove = async (id: string) => {
    try {
      await deleteMasterImage(organization, brickSeries._id, id);
      toast.success("Master image verwijderd");
      onChange();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const uploading = progress != null;

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>Master images</CardTitle>
          <CardDescription>
            Foto's van een gevel waaruit de stenen automatisch worden geknipt.
          </CardDescription>
        </div>
        {masterImages.length > 0 && (
          <FileDropzone
            accept={ACCEPTED_IMAGE_TYPES}
            disabled={uploading}
            onFiles={upload}
            className="h-9 flex-row rounded-md border-solid bg-background px-4 font-medium text-foreground shadow-xs"
          >
            {uploading ? <Spinner /> : <ImageUp className="size-4" />}
            {uploading ? `Uploaden ${Math.round(progress ?? 0)}%` : "Master image uploaden"}
          </FileDropzone>
        )}
      </CardHeader>
      <CardContent>
        {masterImages.length === 0 ? (
          <FileDropzone
            accept={ACCEPTED_IMAGE_TYPES}
            disabled={uploading}
            onFiles={upload}
            className="w-full px-6 py-10"
          >
            {uploading ? (
              <span className="grid w-full max-w-xs gap-2">
                <span>Uploaden {Math.round(progress ?? 0)}%</span>
                <Progress value={progress ?? 0} aria-label="Uploaden" />
              </span>
            ) : (
              <>
                <ImageUp className="size-6" />
                <span className="font-medium text-foreground">Upload een foto van een gevel</span>
                <span>Klik of sleep een bestand hierheen. Tot 30 MB, PNG, JPEG of WebP.</span>
              </>
            )}
          </FileDropzone>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(15rem,1fr))] gap-4">
            {masterImages.map((m) => {
              const unapplied = unappliedChanges(m, bricks);
              const produced = bricks.filter((b) => b.masterImage === m._id).length;
              return (
                <div key={m._id} className="group overflow-hidden rounded-xl border bg-card">
                  <Link
                    to={editorPath(m._id)}
                    className="block overflow-hidden outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                    aria-label="Master image openen in de editor"
                  >
                    <img
                      crossOrigin="anonymous"
                      src={m.preview}
                      alt=""
                      loading="lazy"
                      className="aspect-4/3 w-full bg-muted object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                    />
                  </Link>
                  <div className="grid gap-3 border-t p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="flex items-center gap-1.5 text-sm font-medium tabular-nums">
                        <ScanLine className="size-4 text-muted-foreground" />
                        {m.regions.length} {m.regions.length === 1 ? "regio" : "regio's"}
                      </span>
                      {m.regions.length === 0 ? (
                        <Badge variant="outline" className="text-muted-foreground">
                          Nog niet gedetecteerd
                        </Badge>
                      ) : unapplied === 0 ? (
                        <Badge variant="outline">Bijgewerkt</Badge>
                      ) : (
                        <Badge variant="warning" className="tabular-nums">
                          {unapplied} niet toegepast
                        </Badge>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" className="flex-1" asChild>
                        <Link to={editorPath(m._id)}>Openen in editor</Link>
                      </Button>
                      <ConfirmDialog
                        title="Master image verwijderen?"
                        description={`De ${produced} ${produced === 1 ? "steen" : "stenen"} die eruit ${produced === 1 ? "is" : "zijn"} geknipt, verdwijnen ook uit de collectie.`}
                        confirmLabel="Verwijderen"
                        onConfirm={() => remove(m._id)}
                        trigger={
                          <Button
                            variant="outline"
                            size="icon-sm"
                            className="text-muted-foreground hover:text-destructive"
                            aria-label="Master image verwijderen"
                          >
                            <Trash2 />
                          </Button>
                        }
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MasterImages;
